import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Document, DocumentReader, FieldDataType, View } from 'src/app/modules/core/store/models';
import { DistanceService } from 'src/app/modules/core/services/distance/distance.service';
import * as fromLists from 'src/app/modules/cop/store/lists';
import * as fromCop from 'src/app/modules/cop/store/cop';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { DynamicField } from 'src/app/modules/core/components/fields/dynamic-field';
import { GUID_EMPTY } from '@app/core/constants';
import { Card, CardView, providerIconLookup } from 'src/app/modules/cop/store/lists/models/card.model';
import { DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { UtilService } from 'src/app/modules/cop/services/util.service';
import { DataFormattingService } from 'src/app/modules/cop/services/format/data-formatting.service';
import { RelatedDocumentCard, RelatedDocumentsView } from '../../related-ducument-model';
import { MAX_CARD_VIEW_CONTROLS } from '@app/core/constants';

@Component({
    selector: 'topo-related-person-card',
    templateUrl: './asset-person-card.component.html',
    styleUrls: ['./asset-person-card.component.scss'],
    standalone: false
})
export class RelatedAssetPersonCardComponent implements OnInit {
  @Input() card: RelatedDocumentCard;
  @Input() isActive: boolean;
  @Output() click: EventEmitter<RelatedDocumentCard> = new EventEmitter();
  @Output() zoomToDocument: EventEmitter<RelatedDocumentCard> = new EventEmitter();

  imageUrl: any;
  address: string[];
  location: any;
  summary: string;
  DatasetType = DatasetType;
  DatasetSubtype = DatasetSubtype;
  icon: string;
  iconBgColor: string;
  iconFgColor: string;
  typeName: string;
  postedDateTime: any;
  postedElapsedTime: any;
  documentControls: DynamicField[] = [];
  assetName: string;
  jobTitle: string;
  defaultImageUrl = 'https://files.topo.ai/images/office-image.svg';
  customFieldsSet: boolean;
  truncatedView: View;
  typeNameTranslated: string;
  personName: string;
  guidEmpty = GUID_EMPTY;
  formattedDateTimeOffset: string;

  constructor(private dataFormattingService: DataFormattingService, private store: Store<any>, private utilService: UtilService) {}

  ngOnInit(): void {
    const reader = new DocumentReader(this.card.document);

    this.customFieldsSet = this.card?.view?.controls.length > 0;

    if (this.card?.view?.controls?.length > MAX_CARD_VIEW_CONTROLS) {
      this.truncatedView = {
        ...this.card?.view,
        controls: this.card?.view?.controls.slice(0, MAX_CARD_VIEW_CONTROLS)
      };
    } else {
      this.truncatedView = this.card?.view;
    }

    this.summary =
      reader.name || reader.emailSubject || reader.summary || reader.title || reader.content || reader.getFieldValue('display_name');
    if (this.summary?.length > 50) {
      this.summary = this.summary.slice(0, 50);
    }

    this.address = this.dataFormattingService.buildAddressFromFields(this.card.document);

    // Standard field names are "first_name" and "last_name" but we have also used "first" and "last" previously
    const first = reader.getFieldValue('first_name') ?? reader.getFieldValue('first');
    const last = reader.getFieldValue('last_name') ?? reader.getFieldValue('last');
    if (first && last) {
      this.assetName = first + ' ' + last;
    } else if (first) {
      this.assetName = first;
    } else if (last) {
      this.assetName = last;
    } else {
      this.assetName = reader.name ?? this.summary;
    }

    this.jobTitle = reader.getFieldValue('job_title');

    this.personName = this.assetName?.replace(/^(mr|mrs|miss|ms|dr)[\.\s]*\b/gi, '');

    this.typeName = this.card?.layer?.name;

    if (!!this.card?.dataset?.schema) {
      if (!!reader.getFieldValue('image')) {
        const imageField = this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Image);
        if (!!imageField) {
          this.imageUrl = reader.getFieldValue(imageField.systemName);
        }
      } else {
        this.imageUrl = reader.getFieldValue('image');
      }

      let layerGeoField = this.card?.layer?.displayOptions?.geoFieldsToDisplay?.length > 0 ? this.card?.layer?.displayOptions?.geoFieldsToDisplay[0] : null;

      const geoField =
        this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry && f.systemName === layerGeoField) ??
        this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry && f.settings?.defaultGeometry === true) ??
        this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry);

      if (!!geoField) {
        const geoValue = reader.getFieldValue(geoField.systemName);
        if (!!geoValue) {
          this.location = this.dataFormattingService.getLatLngForDisplay(geoValue);
        } else {
          this.location = null;
        }
      } else {
        this.location = null;
      }
    }

    let posted = reader.posted;

    if (!posted) {
      const val = reader.getFieldValue('published_date');
      posted = !!val ? moment(val) : null;
    }
    if (!posted) {
      const val = reader.getFieldValue('email_date');
      posted = !!val ? moment(val) : null;
    }
    if (!posted) {
      posted = reader.dateCreated;
    }
    if (!!posted) {
      this.postedDateTime = this.dataFormattingService.getPostedDateDisplay(posted);
      this.postedElapsedTime = posted.from(new Date());
    }
  }

  clickCard() {
    this.click.emit(this.card);
  }

  onZoomToDocument() {
    this.zoomToDocument.emit(this.card);
  }
}
